import type { BindingResolver, Recipe } from './bindingResolver.ts';
import type { Session } from './Session.ts';

/**
 * The single source of truth for the TanStack cache-key dimensions
 * shared by every expression-evaluating `useSuspenseQuery` in the app.
 *
 * A result depends on (a) the conversation it runs in (`session.id`) and
 * (b) the position-scoped producer snapshot in scope (`bindings.recipes`)
 * — switching conversations or rendering at a different transcript
 * position / branch must re-key. Callers pass their own `resource`
 * prefix (so distinct hooks never collide) and an opaque `payload` tail
 * (the expression string, or a structured ref descriptor) that further
 * narrows the entry within a scope.
 *
 * Keeping the dimensions here means a new eval-affecting axis is added
 * once, not mirrored by hand across each hook (where one omission would
 * silently diverge cooperating caches).
 */
export function exprQueryKey(
  resource: string,
  session: Session,
  bindings: BindingResolver,
  payload: unknown,
): readonly [string, string, readonly Recipe[], unknown] {
  return [resource, session.id, bindings.recipes, payload] as const;
}

/**
 * Fail-fast query policy shared by the expression hooks. The work is
 * deterministic LLM-authored engine evaluation; the app `QueryClient`'s
 * default retry (3× + exponential backoff) only keeps the component
 * suspended for ~5s before the error reaches the Error Boundary. Mirrors
 * the execute-path `retry: false` on `createChatSession.fetch`.
 */
export const EXPR_FAIL_FAST = { retry: false } as const;
